import { useState } from 'react';
import type { Community } from '@shared/types';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';
import { Banner, Field } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

const empty = {
  name: '',
  address: '',
  managementEmail: '',
  managementPhone: '',
  boardEmail: '',
  boardContactName: '',
  boardContactPhone: '',
};

export function CommunityFields({
  value,
  onChange,
}: {
  value: typeof empty;
  onChange: (next: typeof empty) => void;
}) {
  const set = (key: keyof typeof empty) => (event: { target: { value: string } }) =>
    onChange({ ...value, [key]: event.target.value });

  return (
    <>
      <Field label="Community name">
        <input value={value.name} onChange={set('name')} required placeholder="Willow Creek HOA" />
      </Field>
      <Field label="Community address">
        <input value={value.address} onChange={set('address')} required placeholder="100 Willow Creek Dr, Austin TX" />
      </Field>
      <Field label="Management company email" hint="Leave blank if the board self-manages.">
        <input type="email" value={value.managementEmail} onChange={set('managementEmail')} />
      </Field>
      <Field label="Management company phone">
        <input type="tel" value={value.managementPhone} onChange={set('managementPhone')} />
      </Field>
      <Field label="HOA board email">
        <input type="email" value={value.boardEmail} onChange={set('boardEmail')} />
      </Field>
      <Field label="Main board contact">
        <input value={value.boardContactName} onChange={set('boardContactName')} placeholder="Dana Whitfield, President" />
      </Field>
      <Field label="Board contact phone">
        <input type="tel" value={value.boardContactPhone} onChange={set('boardContactPhone')} />
      </Field>
    </>
  );
}

export function communityToForm(community: Community | null): typeof empty {
  return {
    name: community?.name ?? '',
    address: community?.address ?? '',
    managementEmail: community?.managementEmail ?? '',
    managementPhone: community?.managementPhone ?? '',
    boardEmail: community?.boardEmail ?? '',
    boardContactName: community?.boardContactName ?? '',
    boardContactPhone: community?.boardContactPhone ?? '',
  };
}

export function formToPayload(form: typeof empty) {
  return {
    name: form.name,
    address: form.address,
    managementEmail: form.managementEmail || null,
    managementPhone: form.managementPhone || null,
    boardEmail: form.boardEmail || null,
    boardContactName: form.boardContactName || null,
    boardContactPhone: form.boardContactPhone || null,
  };
}

export function CommunityPage() {
  const { community, setCommunity } = useAuth();
  const { notify, fail } = useToast();
  const [form, setForm] = useState(() => communityToForm(community));
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { community: updated } = await api.updateCommunity(formToPayload(form));
      setCommunity(updated);
      notify('Community details saved.', 'success');
    } catch (error) {
      fail(error, 'Community details were not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Community Management"
        lede="Update how the neighborhood is listed for residents — name, address, and who to call when something is not a portal issue. Disabling a community is a Super Admin action."
      />

      {!community?.address && (
        <Banner kind="warn">Fill in the real community address so violation reports have somewhere to point.</Banner>
      )}

      <form className="card" onSubmit={save}>
        <CommunityFields value={form} onChange={setForm} />
        <div className="btn-row">
          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Saving…' : 'Save community'}
          </button>
        </div>
      </form>
    </div>
  );
}
